import React, { useState, useEffect, useContext } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Bar, Pie, Line, Doughnut, Radar } from "react-chartjs-2";
import axios from "axios";
import Preloader from "../../../shared/PreLoaders";
import { Authorization } from "../../../Context/Authorization";
import { AuthContext } from "../../../Context/AuthContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
  Title
);

const selectStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", cursor: "pointer", transition: "border-color 0.2s",
};
const labelStyle = {
  fontSize: 11, fontWeight: 700, color: "#2D3748", marginBottom: 5,
  display: "block", textTransform: "uppercase", letterSpacing: 0.5,
};

const StatCard = ({ icon, label, value, sub, color, bg }) => (
  <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, padding: "20px 22px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 16 }}>
    <div style={{ width: 52, height: 52, borderRadius: 14, background: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <i className={icon} style={{ fontSize: 24, color }} />
    </div>
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#718096", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: "#1A202C", lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#A0AEC0", marginTop: 3 }}>{sub}</div>}
    </div>
  </div>
);

const MiniStatCard = ({ icon, label, loan, recovered, color, bg }) => (
  <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, padding: "18px 20px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
      <div style={{ width: 40, height: 40, borderRadius: 11, background: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <i className={icon} style={{ fontSize: 20, color }} />
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#1A202C" }}>{label}</div>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
      <div style={{ background: "#F7FAFC", borderRadius: 9, padding: "10px 12px" }}>
        <div style={{ fontSize: 10, color: "#718096", fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>Loan</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#1A202C" }}>₹{loan.toLocaleString("en-IN")}</div>
      </div>
      <div style={{ background: "#F0FFF4", borderRadius: 9, padding: "10px 12px" }}>
        <div style={{ fontSize: 10, color: "#718096", fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>Recovered</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#2E7D32" }}>₹{recovered.toLocaleString("en-IN")}</div>
      </div>
    </div>
  </div>
);

const ChartCard = ({ title, icon, children }) => (
  <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", overflow: "hidden" }}>
    <div style={{ padding: "14px 18px", borderBottom: "1px solid #F0F4F0", display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 32, height: 32, background: "#E8F5E9", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <i className={icon} style={{ color: "#2E7D32", fontSize: 16 }} />
      </div>
      <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>{title}</span>
    </div>
    <div style={{ padding: "16px 18px" }}>{children}</div>
  </div>
);

const alertColors = {
  danger: { bg: "linear-gradient(135deg,#dc3545,#c82333)", icon: "mdi-alert-circle" },
  warning: { bg: "linear-gradient(135deg,#ffc107,#e0a800)", icon: "mdi-alert" },
  success: { bg: "linear-gradient(135deg,#28a745,#218838)", icon: "mdi-check-circle" },
};

const VFCDashboard = () => {
  const { selectedJfmcType } = useContext(AuthContext);
  const [circles, setCircles] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [ranges, setRanges] = useState([]);
  const [vfcs, setVfcs] = useState([]);
  const [viewCharts, setViewCharts] = useState(false);
  const [notificationContent, setNotificationContent] = useState([]);
  const [selectedCircle, setSelectedCircle] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedRange, setSelectedRange] = useState("");
  const [selectedVfc, setSelectedVfc] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const Api = process.env.REACT_APP_API;
  const [dashboardData, setDashboardData] = useState(null);

  /* ── Fetch circles on mount ── */
  useEffect(() => {
    setLoading(true);
    axios.get(`${Api}/api/circles`, Authorization())
      .then((res) => setCircles(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [Api]);

  /* ── Cascading: circle → divisions ── */
  useEffect(() => {
    if (!selectedCircle) return;
    setSelectedDivision(""); setSelectedRange(""); setSelectedVfc("");
    setDivisions([]); setRanges([]); setVfcs([]); setViewCharts(false); setDashboardData(null);
    axios.post(`${Api}/api/divisions/divisions-by-circle`, { circleId: selectedCircle }, Authorization())
      .then((res) => setDivisions(res.data.divisions || res.data))
      .catch(console.error);
  }, [selectedCircle]);

  /* ── Cascading: division → ranges ── */
  useEffect(() => {
    if (!selectedDivision) return;
    setSelectedRange(""); setSelectedVfc("");
    setRanges([]); setVfcs([]); setViewCharts(false); setDashboardData(null);
    axios.post(`${Api}/api/ranges/ranges-by-division`, { divisionId: selectedDivision }, Authorization())
      .then((res) => setRanges(res.data.ranges || res.data))
      .catch(console.error);
  }, [selectedDivision]);

  /* ── Cascading: range → VFCs ── */
  useEffect(() => {
    if (!selectedRange) return;
    setSelectedVfc(""); setVfcs([]); setViewCharts(false); setDashboardData(null);
    axios.post(`${Api}/api/vfcs/vfcs-by-range`, { rangeId: selectedRange, jfmcTypeId: selectedJfmcType?._id }, Authorization())
      .then((res) => setVfcs(res.data.vfcs || res.data))
      .catch(console.error);
  }, [selectedRange, selectedJfmcType]);

  /* ── Build notifications from compliance data ── */
  useEffect(() => {
    if (!dashboardData?.complianceData?.length) { setNotificationContent([]); return; }
    const compliance = dashboardData.complianceData[0];
    const today = new Date();
    const fmt = (d) => new Date(d).toLocaleDateString("en-GB");
    const msgs = [];
    const check = (label, item) => {
      if (item?.nextDate) {
        const nd = new Date(item.nextDate);
        msgs.push({ text: `${label} Next Date: ${fmt(nd)}${today > nd ? " (Overdue)" : ""}`, type: today > nd ? "danger" : "success" });
      } else if (item?.message) {
        msgs.push({ text: item.message, type: "warning" });
      }
    };
    check("Renewal", compliance.renewal);
    check("Audit", compliance.audit);
    check("EC Meeting", compliance.ecMeeting);
    check("General Body", compliance.generalBodyMeeting);
    setNotificationContent(msgs);
  }, [dashboardData]);

  const handleView = async () => {
    if (!selectedVfc) return;
    setLoadingCharts(true);
    try {
      const res = await axios.post(`${Api}/api/vfcs/vfc-dashboard-data`, {
        circleId: selectedCircle,
        divisionId: selectedDivision,
        rangeId: selectedRange,
        vfcId: selectedVfc,
      }, Authorization());
      setDashboardData(res.data);
      setViewCharts(true);
    } catch (err) {
      console.error("Failed to fetch dashboard data", err);
    } finally {
      setLoadingCharts(false);
    }
  };

  const focus = (e) => (e.target.style.borderColor = "#4CAF50");
  const blur = (e) => (e.target.style.borderColor = "#E2E8F0");

  /* ── Derived totals ── */
  const totalFamilies = dashboardData?.vfcs?.reduce((acc, v) => acc + (v.vfcFamily || 0), 0) || 0;
  const vfcDetails = dashboardData?.vfcDataWithDetails || [];
  const totalMembers = vfcDetails.reduce((acc, v) => acc + (v.totalMembers || 0), 0);
  const totalSHG = vfcDetails.reduce((acc, v) => acc + (v.totalSHG || 0), 0);
  const totalLoan = vfcDetails.reduce((acc, v) => acc + (v.totalLoanAmount || 0), 0);
  const loanBalance = dashboardData?.loanBalance || 0;
  const repaymentAmount = dashboardData?.totalRepaymentAmount || 0;

  const loanBreakdown = dashboardData?.vfcDataWithDetails?.[0]?.loanTypeBreakdown || [];
  const deptData = loanBreakdown.find((l) => l.loanTypeName?.toLowerCase().includes("department")) || {};
  const revData = loanBreakdown.find((l) => l.loanTypeName?.toLowerCase().includes("revolving")) || {};

  /* ── Chart data ── */
  const COLORS = ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40"];

  const barData = {
    labels: dashboardData?.yearWiseLoanData?.labels || [],
    datasets: [{ label: "Loan Issued", data: dashboardData?.yearWiseLoanData?.loanIssued || [], backgroundColor: "rgba(46,125,50,0.75)", borderRadius: 6 }],
  };

  const lineData = {
    labels: dashboardData?.monthlyLoanData?.labels || [],
    datasets: [
      { label: "Loan Issued", data: dashboardData?.monthlyLoanData?.loanIssued || [], borderColor: "#2E7D32", backgroundColor: "rgba(46,125,50,0.08)", fill: true, tension: 0.4, pointBackgroundColor: "#2E7D32" },
      { label: "Repayments", data: dashboardData?.monthlyLoanData?.repayments || [], borderColor: "#FF6384", fill: false, tension: 0.4, pointBackgroundColor: "#FF6384" },
    ],
  };

  const doughnutData = {
    labels: ["Loan Amount", "Recovered", "Balance"],
    datasets: [{ data: [totalLoan, repaymentAmount, loanBalance], backgroundColor: ["#1B5E20", "#4CAF50", "#A5D6A7"], borderWidth: 0 }],
  };

  const radarData = {
    labels: ["Members", "SHG", "Loan Amount"],
    datasets: [{
      label: vfcDetails.length === 1 ? vfcDetails[0].vfcName : "Combined VFCs",
      data: [totalMembers, totalSHG, totalLoan],
      backgroundColor: "rgba(46,125,50,0.2)", borderColor: "#2E7D32", borderWidth: 2, pointBackgroundColor: "#2E7D32",
    }],
  };

  const performanceScale = ["Needs Improvement", "Below Average", "Moderately Performing", "High Performing"];
  const perfColors = ["#e74c3c", "#e67e22", "#f1c40f", "#2ecc71"];
  const perfIdx = performanceScale.indexOf(dashboardData?.performanceStatus ?? "");
  const pieData = {
    labels: performanceScale,
    datasets: [{
      label: "Performance Status",
      data: performanceScale.map((_, i) => i === perfIdx ? 1 : 0),
      backgroundColor: performanceScale.map((_, i) => i === perfIdx ? perfColors[i] : "#E2E8F0"),
      borderWidth: 0,
    }],
  };

  const chartOpts = { responsive: true, maintainAspectRatio: true, plugins: { legend: { position: "bottom" } } };

  const selectedVfcName = vfcs.find((v) => v._id === selectedVfc)?.vfcName || "VFC";

  if (loading) return <Preloader loading />;

  return (
    <div>
      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>VFC Dashboard</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>VFC</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>VFC Dashboard</span>
        </div>
      </div>

      {/* ── No JFMC Type warning ── */}
      {!selectedJfmcType && (
        <div style={{ background: "#FFF5F5", border: "1px solid #FED7D7", borderRadius: 12, padding: "18px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 40, height: 40, background: "#FFF5F5", borderRadius: "50%", border: "2px solid #FC8181", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 20, color: "#E53E3E" }} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#C53030" }}>No JFMC Type Selected</div>
            <div style={{ fontSize: 12, color: "#718096", marginTop: 2 }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar to continue.</div>
          </div>
        </div>
      )}

      {/* ── Filter Card ── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E2E8F0", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", overflow: "hidden", marginBottom: 24 }}>
        <div style={{ padding: "12px 18px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="mdi mdi-filter-outline" style={{ color: "#fff", fontSize: 16 }} />
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Select VFC to View Dashboard</span>
        </div>

        <div style={{ padding: "20px 18px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr) auto", gap: 14, alignItems: "flex-end" }}>

            <div>
              <label style={labelStyle}>Circle</label>
              <select value={selectedCircle} onChange={(e) => setSelectedCircle(e.target.value)}
                style={selectStyle} onFocus={focus} onBlur={blur}>
                <option value="">Select Circle</option>
                {[...circles].sort((a, b) => a.circleName.localeCompare(b.circleName)).map((c) => (
                  <option key={c._id} value={c._id}>{c.circleName}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Division</label>
              <select value={selectedDivision} onChange={(e) => setSelectedDivision(e.target.value)}
                style={{ ...selectStyle, opacity: !selectedCircle ? 0.5 : 1 }}
                disabled={!selectedCircle} onFocus={focus} onBlur={blur}>
                <option value="">Select Division</option>
                {[...divisions].sort((a, b) => a.divisionName.localeCompare(b.divisionName)).map((d) => (
                  <option key={d._id} value={d._id}>{d.divisionName}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Range</label>
              <select value={selectedRange} onChange={(e) => setSelectedRange(e.target.value)}
                style={{ ...selectStyle, opacity: !selectedDivision ? 0.5 : 1 }}
                disabled={!selectedDivision} onFocus={focus} onBlur={blur}>
                <option value="">Select Range</option>
                {[...ranges].sort((a, b) => a.rangeName.localeCompare(b.rangeName)).map((r) => (
                  <option key={r._id} value={r._id}>{r.rangeName}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>VFC</label>
              <select value={selectedVfc} onChange={(e) => setSelectedVfc(e.target.value)}
                style={{ ...selectStyle, opacity: !selectedRange ? 0.5 : 1 }}
                disabled={!selectedRange} onFocus={focus} onBlur={blur}>
                <option value="">Select VFC</option>
                {[...vfcs].sort((a, b) => a.vfcName.localeCompare(b.vfcName)).map((v) => (
                  <option key={v._id} value={v._id}>{v.vfcName}</option>
                ))}
              </select>
            </div>

            <div>
              <button
                disabled={!selectedVfc || loadingCharts}
                onClick={handleView}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 7,
                  padding: "10px 20px",
                  background: selectedVfc ? "linear-gradient(135deg,#1B5E20,#2E7D32)" : "#E2E8F0",
                  color: selectedVfc ? "#fff" : "#A0AEC0",
                  border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700,
                  cursor: selectedVfc ? "pointer" : "not-allowed",
                  boxShadow: selectedVfc ? "0 3px 10px rgba(46,125,50,0.3)" : "none",
                  whiteSpace: "nowrap",
                }}>
                {loadingCharts
                  ? <><i className="mdi mdi-loading mdi-spin" /> Loading…</>
                  : <><i className="mdi mdi-eye" /> View</>}
              </button>
            </div>
          </div>

          {/* Progress indicator */}
          <div style={{ display: "flex", gap: 6, marginTop: 14, alignItems: "center" }}>
            {["Circle", "Division", "Range", "VFC"].map((step, i) => {
              const done = [selectedCircle, selectedDivision, selectedRange, selectedVfc][i];
              return (
                <React.Fragment key={step}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <div style={{ width: 20, height: 20, borderRadius: "50%", background: done ? "#2E7D32" : "#E2E8F0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: done ? "#fff" : "#A0AEC0", fontWeight: 700 }}>
                      {done ? <i className="mdi mdi-check" style={{ fontSize: 11 }} /> : i + 1}
                    </div>
                    <span style={{ fontSize: 11, color: done ? "#2E7D32" : "#A0AEC0", fontWeight: done ? 600 : 400 }}>{step}</span>
                  </div>
                  {i < 3 && <div style={{ flex: 1, height: 1, background: done ? "#4CAF50" : "#E2E8F0", maxWidth: 40 }} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Dashboard Content ── */}
      {!viewCharts ? (
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, padding: "60px 20px", textAlign: "center", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
          <i className="mdi mdi-view-dashboard-outline" style={{ fontSize: 56, color: "#CBD5E0", display: "block", marginBottom: 16 }} />
          <div style={{ fontSize: 16, fontWeight: 700, color: "#2D3748", marginBottom: 8 }}>Select a VFC to View Dashboard</div>
          <div style={{ fontSize: 13, color: "#718096" }}>Choose Circle → Division → Range → VFC from the filters above, then click View.</div>
        </div>
      ) : (
        <>
          {/* VFC Name banner */}
          <div style={{ background: "linear-gradient(135deg,#1B5E20,#2E7D32)", borderRadius: 14, padding: "16px 22px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Viewing Dashboard for</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", marginTop: 2 }}>{selectedVfcName}</div>
            </div>
            <button onClick={() => { setViewCharts(false); setDashboardData(null); }}
              style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", borderRadius: 8, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              <i className="mdi mdi-refresh" /> Change VFC
            </button>
          </div>

          {/* ── Stat Cards Row 1 ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 16 }}>
            <StatCard icon="mdi mdi-home-group" label="Total Families" value={totalFamilies.toLocaleString("en-IN")} sub="Active Families" color="#1B5E20" bg="#E8F5E9" />
            <StatCard icon="mdi mdi-account-group" label="Total Members" value={totalMembers.toLocaleString("en-IN")} sub="Registered Members" color="#1565C0" bg="#E3F2FD" />
            <StatCard icon="mdi mdi-account-multiple-check" label="Total SHGs" value={totalSHG.toLocaleString("en-IN")} sub="Self Help Groups" color="#E65100" bg="#FFF3E0" />
            <StatCard icon="mdi mdi-currency-inr" label="Total Loan Amount" value={`₹${totalLoan.toLocaleString("en-IN")}`} sub="Amount" color="#6A1B9A" bg="#F3E5F5" />
          </div>

          {/* ── Mini Stat Cards Row 2 ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 22 }}>
            <MiniStatCard icon="mdi mdi-bank" label="Department Loan" loan={deptData.totalAmount || 0} recovered={deptData.recoveredAmount || 0} color="#C53030" bg="#FFF5F5" />
            <MiniStatCard icon="mdi mdi-sync" label="Revolving Fund" loan={revData.totalAmount || 0} recovered={revData.recoveredAmount || 0} color="#1565C0" bg="#E3F2FD" />
          </div>

          {/* ── Compliance Notifications ── */}
          {notificationContent.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginBottom: 22 }}>
              {notificationContent.map((item, i) => {
                const cfg = alertColors[item.type] || alertColors.success;
                return (
                  <div key={i} style={{ padding: "14px 18px", borderRadius: 10, color: "#fff", fontWeight: 500, boxShadow: "0 4px 12px rgba(0,0,0,0.12)", background: cfg.bg, display: "flex", alignItems: "center", gap: 10 }}>
                    <i className={`mdi ${cfg.icon}`} style={{ fontSize: 18 }} />
                    <span style={{ fontSize: 13 }}>{item.text}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Charts Row 1 ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
            <ChartCard title="VFC Rating" icon="mdi mdi-radar">
              <Radar data={radarData} options={chartOpts} />
            </ChartCard>
            <ChartCard title="Loan Status" icon="mdi mdi-chart-pie">
              <Doughnut data={doughnutData} options={chartOpts} />
            </ChartCard>
          </div>

          {/* ── Charts Row 2 ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
            <ChartCard title="Year-wise Loan Issued" icon="mdi mdi-chart-bar">
              <Bar data={barData} options={chartOpts} />
            </ChartCard>
            <ChartCard title="Performance Evaluation" icon="mdi mdi-chart-donut">
              <Pie data={pieData} options={chartOpts} />
            </ChartCard>
          </div>

          {/* ── Chart Row 3 ── */}
          <ChartCard title="Monthly Loan Issue & Repayment" icon="mdi mdi-chart-line">
            <Line data={lineData} options={{ ...chartOpts, maintainAspectRatio: false }} height={260} />
          </ChartCard>
        </>
      )}
    </div>
  );
};

export default VFCDashboard;
